import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  fetchAppointmentStats,
  fetchMessageStats,
  fetchOrderStats,
  fetchPatientHelpStats,
  setPaymentOverdue,
} from 'app/shared-components/store/pharmacySelectorSlice';
import { getMessagingToken, subscribe } from 'app/services/firebase';
import coreAPI from 'apis/core';
import queryClient from 'app/queryClient';
import { DEPLOYMENT_REGIONS, deploymentRegion } from 'app/constants';

import { getUnreadClinicMessagesKey } from 'app/pages/Prescribing/pages/OrdersUnreadMessagesList/hooks/useUnreadClinicMessages';
import { getClinicMessageStatsKey } from 'app/pages/Prescribing/hooks/useClinicMessageStats';
import { getOrderMessageThreadKey } from 'app/pages/Prescribing/hooks/useOrderMessageThread';
import { playNotificationBeep } from './playNotificationBeep';
import { resetFavicon, setFaviconBadge } from './faviconBadge';
import { onPatientMessagesRead } from './patientMessagesRead';

// Message pings ship for the Canada deployment only (HEA-659); the UK keeps
// the previous behaviour of a plain toast on message pushes
const isCanadaDeployment = deploymentRegion === DEPLOYMENT_REGIONS.CA;

async function getFcmToken() {
  const fcmToken = await getMessagingToken();
  if (fcmToken) coreAPI.auth.updateToken({ fcm_token: fcmToken });
}

function handleMessageNotification(message) {
  // Invalidate all relevant queries, so that if prescriber
  // is on any page related to messages, they will be re-fetched
  queryClient.invalidateQueries({
    queryKey: ['clinicOrder', message.orderId, 'messageThread'],
  });
  queryClient.invalidateQueries({ queryKey: ['order-messages', message.messageThreadId] });
  queryClient.invalidateQueries({ queryKey: getUnreadClinicMessagesKey(message.clinicId) });
  queryClient.invalidateQueries({ queryKey: getClinicMessageStatsKey(message.clinicId) });
  queryClient.invalidateQueries({ queryKey: getOrderMessageThreadKey(message.orderId) });
}

export default function Notification() {
  const user = useSelector(({ auth }) => auth.user);
  const selectedPharmacyId = useSelector(
    ({ pharmacySelector }) => pharmacySelector.selectedPharmacyId
  );
  const selectedPharmacyChainId = useSelector(
    ({ pharmacySelector }) => pharmacySelector.selectedPharmacy?.chain_id
  );
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const location = useLocation();

  // Refs for notification state (avoids stale closures in subscribe callback)
  const isOnOrdersPageRef = useRef(false);
  const unseenCountsRef = useRef({ order: 0, message: 0 });
  const originalTitleRef = useRef(document.title);

  isOnOrdersPageRef.current = location.pathname === '/orders';

  // Reflect the combined unseen count in the tab title and favicon
  const updateBadges = useCallback(() => {
    const total = unseenCountsRef.current.order + unseenCountsRef.current.message;
    if (total > 0) {
      document.title = `(${total}) ${originalTitleRef.current}`;
      setFaviconBadge(total);
    } else {
      document.title = originalTitleRef.current;
      resetFavicon();
    }
  }, []);

  // Reset unseen counts when the user switches pharmacy
  useEffect(() => {
    unseenCountsRef.current = { order: 0, message: 0 };
    updateBadges();
  }, [selectedPharmacyId, updateBadges]);

  // Clear the order count when the user visits the page where orders are read
  useEffect(() => {
    if (location.pathname === '/orders') {
      unseenCountsRef.current.order = 0;
      updateBadges();
    }
  }, [location.pathname, updateBadges]);

  // Being on /patients does not mean messages were read; only clear the
  // message count when the user actually opens a patient's message thread
  useEffect(() => {
    return onPatientMessagesRead(() => {
      unseenCountsRef.current.message = 0;
      updateBadges();
    });
  }, [updateBadges]);

  // Restore document title and favicon on unmount
  useEffect(() => {
    const savedTitle = originalTitleRef.current;
    return () => {
      document.title = savedTitle;
      resetFavicon();
    };
  }, []);

  const handleMessage = useCallback(
    (message) => {
      if (message.type === 'order-message') {
        handleMessageNotification(message);
        return;
      }

      if (message.type === 'payment_overdue') {
        if (selectedPharmacyChainId && message.organisation_id === selectedPharmacyChainId) {
          dispatch(setPaymentOverdue(message.payment_overdue === 'true'));
        }
        return;
      }

      if (message.pharmacy_id === selectedPharmacyId) {
        switch (message.type) {
          case 'message':
            dispatch(fetchMessageStats());
            if (isCanadaDeployment) queryClient.invalidateQueries({ queryKey: ['patients'] });
            break;
          case 'appointment':
            dispatch(fetchAppointmentStats());
            break;
          case 'order':
            dispatch(fetchOrderStats());
            break;
          case 'issue':
            dispatch(fetchPatientHelpStats());
            break;
          default:
        }
      }
    },
    [dispatch, selectedPharmacyId, selectedPharmacyChainId]
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (user?._id) {
      getFcmToken();
      return subscribe((message) => {
        handleMessage(message);

        const isCurrentPharmacyOrder =
          message.type === 'order' && message.pharmacy_id === selectedPharmacyId;
        const isCurrentPharmacyMessage =
          isCanadaDeployment &&
          message.type === 'message' &&
          message.pharmacy_id === selectedPharmacyId;

        if (isCurrentPharmacyOrder) {
          playNotificationBeep();

          if (isOnOrdersPageRef.current) {
            enqueueSnackbar('You have received a new order. Please refresh the page to see it.', {
              variant: 'info',
              preventDuplicate: true,
            });
          } else {
            unseenCountsRef.current.order += 1;
            updateBadges();
            enqueueSnackbar(message.title, { variant: 'info' });
          }
        } else if (isCurrentPharmacyMessage) {
          playNotificationBeep();
          unseenCountsRef.current.message += 1;
          updateBadges();
          enqueueSnackbar(message.title, { variant: 'info' });
        } else {
          enqueueSnackbar(message.title, { variant: 'info' });
        }
      });
    }
  }, [enqueueSnackbar, handleMessage, selectedPharmacyId, updateBadges, user?._id]);

  return <></>;
}
